import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { useLanguage } from '@/App';
import { API } from '@/App';
import { seoData } from '@/utils/seoData';
import { MapPin, Clock, Upload, FileText, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const MAX_CV_SIZE_MB = 5;
const ALLOWED_CV_EXTENSIONS = ['.pdf', '.doc', '.docx'];

const CareerPage = () => {
  const { language } = useLanguage();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [cvFile, setCvFile] = useState(null);
  const [cvUploading, setCvUploading] = useState(false);
  const [cvUploadedUrl, setCvUploadedUrl] = useState('');
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    cover_letter: '',
  });

  const content = {
    tr: {
      title: 'Kariyer',
      subtitle: 'Ekibimize katılın ve birlikte büyüyelim',
      loading: 'Yükleniyor...',
      apply: 'Başvur',
      applyTitle: 'İş Başvurusu',
      form: {
        name: 'Adınız Soyadınız',
        email: 'E-posta',
        phone: 'Telefon',
        cv: 'CV Dosyası',
        cvHint: `PDF, DOC veya DOCX (maks. ${MAX_CV_SIZE_MB}MB)`,
        cvSelect: 'CV Dosyası Seçin',
        cvUploading: 'Yükleniyor...',
        coverLetter: 'Başvuru Mektubu (Opsiyonel)',
        submit: 'Başvuruyu Gönder',
        submitting: 'Gönderiliyor...',
        success: 'Başvurunuz başarıyla alındı!',
        duplicate: 'Bu pozisyona daha önce başvuruda bulundunuz.',
        errorSize: `Dosya boyutu en fazla ${MAX_CV_SIZE_MB}MB olabilir.`,
        errorType: 'Sadece PDF, DOC veya DOCX dosyaları kabul edilmektedir.',
        errorUpload: 'CV yüklenirken hata oluştu. Lütfen tekrar deneyin.',
        errorGeneral: 'Bir hata oluştu. Lütfen tekrar deneyin.',
      },
      noJobs: 'Şu anda açık pozisyon bulunmamaktadır.',
      requirements: 'Gereksinimler',
    },
    en: {
      title: 'Career',
      subtitle: 'Join our team and grow together',
      loading: 'Loading...',
      apply: 'Apply',
      applyTitle: 'Job Application',
      form: {
        name: 'Full Name',
        email: 'Email',
        phone: 'Phone',
        cv: 'CV File',
        cvHint: `PDF, DOC or DOCX (max. ${MAX_CV_SIZE_MB}MB)`,
        cvSelect: 'Select CV File',
        cvUploading: 'Uploading...',
        coverLetter: 'Cover Letter (Optional)',
        submit: 'Submit Application',
        submitting: 'Submitting...',
        success: 'Your application has been received!',
        duplicate: 'You have already applied for this position.',
        errorSize: `File size must not exceed ${MAX_CV_SIZE_MB}MB.`,
        errorType: 'Only PDF, DOC or DOCX files are accepted.',
        errorUpload: 'Failed to upload CV. Please try again.',
        errorGeneral: 'An error occurred. Please try again.',
      },
      noJobs: 'No open positions at the moment.',
      requirements: 'Requirements',
    },
  };

  const t = content[language];

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/jobs?active_only=true`);
      setJobs(response.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = (job) => {
    setSelectedJob(job);
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', phone: '', cover_letter: '' });
    setCvFile(null);
    setCvUploadedUrl('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDialogChange = (open) => {
    setDialogOpen(open);
    if (!open) resetForm();
  };

  const validateCvFile = (file) => {
    // Check extension
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!ALLOWED_CV_EXTENSIONS.includes(ext)) {
      toast.error(t.form.errorType);
      return false;
    }
    // Check size
    if (file.size > MAX_CV_SIZE_MB * 1024 * 1024) {
      toast.error(t.form.errorSize);
      return false;
    }
    return true;
  };

  const handleCvSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!validateCvFile(file)) {
      e.target.value = '';
      return;
    }

    setCvFile(file);
    setCvUploading(true);
    setCvUploadedUrl('');

    try {
      const uploadData = new FormData();
      uploadData.append('file', file);

      const response = await axios.post(`${API}/upload-cv`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data?.url) {
        setCvUploadedUrl(response.data.url);
      } else {
        throw new Error('No URL returned');
      }
    } catch (error) {
      console.error('CV upload error:', error);
      const detail = error.response?.data?.detail;
      if (error.response?.status === 413) {
        toast.error(t.form.errorSize);
      } else if (error.response?.status === 400) {
        toast.error(detail || t.form.errorType);
      } else {
        toast.error(t.form.errorUpload);
      }
      setCvFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } finally {
      setCvUploading(false);
    }
  };

  const removeCv = () => {
    setCvFile(null);
    setCvUploadedUrl('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!cvUploadedUrl) {
      toast.error(language === 'tr' ? 'Lütfen CV dosyanızı yükleyin.' : 'Please upload your CV file.');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/job-applications`, {
        ...formData,
        job_id: selectedJob.id,
        cv_url: cvUploadedUrl,
      });
      toast.success(t.form.success);
      handleDialogChange(false);
    } catch (error) {
      console.error('Application error:', error);
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409) {
        toast.error(detail || t.form.duplicate);
      } else if (error.response?.status === 429) {
        toast.error(detail || t.form.errorGeneral);
      } else {
        toast.error(detail || t.form.errorGeneral);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const pageSeo = seoData.career[language];

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={pageSeo.title}
        description={pageSeo.description}
        keywords={pageSeo.keywords}
      />
      <Navbar />

      {/* Hero */}
      <section className="pt-24 pb-16 px-4 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-4xl mx-auto text-center">
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            data-testid="career-title"
          >
            {t.title}
          </h1>
          <p className="text-lg sm:text-xl text-gray-600" data-testid="career-subtitle">{t.subtitle}</p>
        </div>
      </section>

      {/* Jobs Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          {loading ? (
            <div className="text-center text-gray-600">{t.loading}</div>
          ) : jobs.length === 0 ? (
            <div className="text-center text-gray-600">{t.noJobs}</div>
          ) : (
            <div className="space-y-6">
              {jobs.map((job) => (
                <div
                  key={job.id}
                  className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow"
                  data-testid={`job-card-${job.id}`}
                >
                  <h3 className="text-2xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {language === 'tr' ? job.title_tr : job.title_en}
                  </h3>

                  <div className="flex flex-wrap gap-4 mb-4 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      <span>{job.location}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{job.type}</span>
                    </div>
                  </div>

                  <p className="text-gray-700 mb-4">
                    {language === 'tr' ? job.description_tr : job.description_en}
                  </p>

                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-900 mb-2">{t.requirements}:</h4>
                    <p className="text-gray-700 whitespace-pre-line">
                      {language === 'tr' ? job.requirements_tr : job.requirements_en}
                    </p>
                  </div>

                  <Button onClick={() => handleApply(job)} data-testid={`apply-job-${job.id}`}>
                    {t.apply}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Application Dialog */}
      <Dialog open={dialogOpen} onOpenChange={handleDialogChange}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t.applyTitle}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">{t.form.name}</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                data-testid="application-name"
              />
            </div>
            <div>
              <Label htmlFor="email">{t.form.email}</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                data-testid="application-email"
              />
            </div>
            <div>
              <Label htmlFor="phone">{t.form.phone}</Label>
              <Input
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                data-testid="application-phone"
              />
            </div>

            {/* CV Upload */}
            <div>
              <Label>{t.form.cv} <span className="text-red-500">*</span></Label>
              <p className="text-xs text-gray-500 mb-2">{t.form.cvHint}</p>

              {!cvFile ? (
                <label
                  className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors"
                >
                  <Upload className="w-6 h-6 text-gray-400 mb-1" />
                  <span className="text-sm text-gray-500">{t.form.cvSelect}</span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleCvSelect}
                    className="hidden"
                    data-testid="application-cv"
                  />
                </label>
              ) : (
                <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <FileText className="w-8 h-8 text-blue-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{cvFile.name}</p>
                    <p className="text-xs text-gray-500">{formatFileSize(cvFile.size)}</p>
                  </div>
                  {cvUploading ? (
                    <Loader2 className="w-5 h-5 text-blue-600 animate-spin flex-shrink-0" />
                  ) : cvUploadedUrl ? (
                    <span className="text-green-600 text-xs font-medium flex-shrink-0">OK</span>
                  ) : null}
                  {!cvUploading && (
                    <button
                      type="button"
                      onClick={removeCv}
                      className="p-1 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="cover_letter">{t.form.coverLetter}</Label>
              <Textarea
                id="cover_letter"
                name="cover_letter"
                value={formData.cover_letter}
                onChange={handleChange}
                rows={4}
                data-testid="application-cover"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting || cvUploading || !cvUploadedUrl}
              className="w-full"
              data-testid="application-submit"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t.form.submitting}</>
              ) : (
                t.form.submit
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default CareerPage;
